import type { ChainId, Environment, Network } from 'types';

import { apiHosts } from './apiUrls';
import { envVariables } from './envVariables';
import { assertLocalDeployAddressesAreUsable } from './localAddresses';
import { rpcUrls } from './rpcUrls';
import { getGovernanceSubgraphUrls } from './subgraphUrls';
import { subtensorWsUrls } from './subtensorWsUrls';
import { assertTestnetDeployAddressesAreUsable } from './testnetAddresses';

export interface Config {
  environment: Environment;
  network: Network;
  isSafeApp: boolean;
  apiUrl: string;
  wsApiUrl: string;
  rpcUrls: {
    [chainId in ChainId]: string[];
  };
  subtensorWsUrls: Partial<Record<ChainId, string>>;
  governanceSubgraphUrls: {
    [chainId in ChainId]: string | undefined;
  };
  sentryDsn: string;
  posthog: {
    apiKey: string;
    hostUrl: string;
  };
  googleAnalytics: {
    measurementId: string;
  };
  zyFiApiKey: string;
  biconomyApiKey: string;
  safeApiKey: string;
}

const environment = (envVariables.VITE_ENV || 'preview') as Environment;
const network = (envVariables.VITE_NETWORK || 'mainnet') as Network;

const forbiddenLocalEnvVariableNames = [
  'VITE_SENTRY_DSN',
  'VITE_POSTHOG_API_KEY',
  'VITE_POSTHOG_HOST_URL',
  'VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID',
  'VITE_ZYFI_API_KEY',
  'VITE_ALCHEMY_API_KEY',
  'VITE_NODE_REAL_API_KEY',
  'VITE_THE_GRAPH_API_KEY',
  'VITE_BICONOMY_API_KEY',
  'VITE_SAFE_API_KEY',
] as const;

if (network === 'local') {
  assertLocalDeployAddressesAreUsable();

  const configuredForbiddenEnvVariables = forbiddenLocalEnvVariableNames.filter(
    (envVariableName) => !!envVariables[envVariableName],
  );

  if (configuredForbiddenEnvVariables.length > 0) {
    throw new Error(
      `VITE_NETWORK=local forbids third-party env variables: ${configuredForbiddenEnvVariables.join(', ')}`,
    );
  }
}

if (network === 'bittensor_testnet') {
  assertTestnetDeployAddressesAreUsable();
}

const apiHost = apiHosts[network];
const apiUrl = apiHost ? `https://${apiHost}` : '';
const wsApiUrl = apiHost ? `wss://${apiHost}/ws` : '';

const disablesThirdPartyConfig = network === 'local';
const defaultGoogleAnalyticsMeasurementId = 'G-8MF40YZG8Z';

const keys = {
  nodeRealApiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_NODE_REAL_API_KEY,
  theGraphApiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_THE_GRAPH_API_KEY,
};

const governanceSubgraphUrls = getGovernanceSubgraphUrls(keys);

const isSafeApp = window?.location.ancestorOrigins?.[0] === 'https://app.safe.global';

const config: Config = {
  environment,
  network,
  isSafeApp,
  apiUrl,
  wsApiUrl,
  rpcUrls,
  subtensorWsUrls,
  governanceSubgraphUrls,
  sentryDsn: disablesThirdPartyConfig ? '' : envVariables.VITE_SENTRY_DSN || '',
  posthog: {
    apiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_POSTHOG_API_KEY || '',
    hostUrl: disablesThirdPartyConfig ? '' : envVariables.VITE_POSTHOG_HOST_URL || '',
  },
  googleAnalytics: {
    measurementId: disablesThirdPartyConfig
      ? ''
      : envVariables.VITE_GOOGLE_ANALYTICS_MEASUREMENT_ID || defaultGoogleAnalyticsMeasurementId,
  },
  zyFiApiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_ZYFI_API_KEY || '',
  biconomyApiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_BICONOMY_API_KEY || '',
  safeApiKey: disablesThirdPartyConfig ? '' : envVariables.VITE_SAFE_API_KEY || '',
};

export { envVariables } from './envVariables';
export default config;
